import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, FileText, DollarSign, Clock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPContracts = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [runningBills, setRunningBills] = useState([]);
  const [variationOrders, setVariationOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contracts');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, contractsRes, billsRes, vosRes, projectsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/contracts/stats`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/contracts/contracts`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/contracts/running-bills`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/contracts/variation-orders`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/projects`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setContracts(contractsRes.data);
      setRunningBills(billsRes.data);
      setVariationOrders(vosRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contracts & Billing</h1>
          <p className="text-gray-600 mt-1">Manage contracts, running bills, and variation orders</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Contracts</p><p className="text-2xl font-bold text-gray-900">{stats?.total_contracts || 0}</p></div><FileText className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Active</p><p className="text-2xl font-bold text-green-600">{stats?.active_contracts || 0}</p></div><FileText className="w-10 h-10 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Running Bills</p><p className="text-2xl font-bold text-gray-900">{stats?.total_running_bills || 0}</p></div><DollarSign className="w-10 h-10 text-purple-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Pending Bills</p><p className="text-2xl font-bold text-orange-600">{stats?.pending_bills || 0}</p></div><Clock className="w-10 h-10 text-orange-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Variation Orders</p><p className="text-2xl font-bold text-gray-900">{stats?.total_variation_orders || 0}</p></div><FileText className="w-10 h-10 text-indigo-500" /></div></CardContent></Card>
      </div>

      <div className="mb-6 border-b">
        <div className="flex gap-4">
          <button onClick={() => setActiveTab('contracts')} className={`pb-2 px-4 font-medium ${activeTab === 'contracts' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Contracts</button>
          <button onClick={() => setActiveTab('bills')} className={`pb-2 px-4 font-medium ${activeTab === 'bills' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Running Bills</button>
          <button onClick={() => setActiveTab('vo')} className={`pb-2 px-4 font-medium ${activeTab === 'vo' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Variation Orders</button>
        </div>
      </div>

      {activeTab === 'contracts' && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">All Contracts</h2>
            <div className="space-y-3">
              {contracts.map(contract => (
                <div key={contract.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{contract.contract_number}</h3>
                      <p className="text-sm text-gray-600">{contract.client_name}</p>
                      <p className="text-xs text-gray-500 mt-1">{contract.start_date} to {contract.end_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">₹{(contract.contract_value / 10000000).toFixed(2)}Cr</p>
                      <p className="text-xs text-gray-600">Retention: {contract.retention_percentage}%</p>
                      <span className="mt-2 inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded">{contract.status}</span>
                    </div>
                  </div>
                </div>
              ))}
              {contracts.length === 0 && <div className="text-center py-8 text-gray-500">No contracts yet</div>}
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'bills' && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Running Bills (RA Bills)</h2>
            <div className="space-y-3">
              {runningBills.map(bill => (
                <div key={bill.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{bill.bill_number}</h3>
                      <p className="text-sm text-gray-600">{bill.description}</p>
                      <p className="text-xs text-gray-500">{bill.from_date} to {bill.to_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Current Bill: ₹{(bill.current_bill_value / 100000).toFixed(2)}L</p>
                      <p className="text-sm text-red-600">Retention: -₹{(bill.retention_deducted / 100000).toFixed(2)}L</p>
                      <p className="font-bold text-green-600">Net: ₹{(bill.net_payable / 100000).toFixed(2)}L</p>
                      <span className={`mt-2 inline-block px-2 py-1 text-xs rounded ${bill.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{bill.status}</span>
                    </div>
                  </div>
                </div>
              ))}
              {runningBills.length === 0 && <div className="text-center py-8 text-gray-500">No running bills yet</div>}
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'vo' && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Variation Orders</h2>
            <div className="space-y-3">
              {variationOrders.map(vo => (
                <div key={vo.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold">{vo.vo_number}</h3>
                      <p className="text-sm text-gray-600">{vo.description}</p>
                      <p className="text-xs text-gray-500 mt-1">Reason: {vo.reason}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">₹{(vo.amount / 100000).toFixed(2)}L</p>
                      <span className={`mt-2 inline-block px-2 py-1 text-xs rounded ${vo.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{vo.status}</span>
                    </div>
                  </div>
                </div>
              ))}
              {variationOrders.length === 0 && <div className="text-center py-8 text-gray-500">No variation orders yet</div>}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ERPContracts;